import type { ICreateSubscriptionCmd } from "@/data/commands/subscription/CreateSubscriptionCmd";
import type { ISubscription, ISubscriptionPreset } from "@/data/model/Subscription";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import type { ISubscriptionsQry } from "@/data/queries/subscription/SubscriptionsQry";
import { DateTime } from "@/data/util/DateTime";

import {
    BaseServiceDao,
    type IDataSource,
    type IEditableDataSource,
    type IPaginatedResult,
    parsePaginatedResult,
} from "./BaseServiceDao";

export class SubscriptionDao extends BaseServiceDao implements IEditableDataSource<ISubscription> {
    public static className = "SubscriptionDao";

    constructor() {
        super("subscription");
    }

    public async getSingle(id: string): Promise<ISubscription> {
        return this.get(id, undefined, SubscriptionDao.parseSubscription);
    }

    public async getData(qry: ISubscriptionsQry): Promise<IPaginatedResult<ISubscription>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, SubscriptionDao.parseSubscription));
    }

    public async create(cmd: ICreateSubscriptionCmd): Promise<ISubscription> {
        return this.post("", cmd, SubscriptionDao.parseSubscription);
    }

    public async update(id: string, cmd: ICreateSubscriptionCmd): Promise<ISubscription> {
        return this.put(id, cmd, SubscriptionDao.parseSubscription);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, {});
    }

    public async getPresets(
        qry: IPaginatedQry,
        abortSignal?: AbortSignal
    ): Promise<IPaginatedResult<ISubscriptionPreset>> {
        return this.get("preset/", qry, (f) => parsePaginatedResult(f, SubscriptionDao.parsePreset), abortSignal);
    }

    public presetDataSource: IDataSource<ISubscriptionPreset> = {
        getData: (filter, abortSignal) => this.getPresets(filter, abortSignal),
    };

    public static parseSubscription(literal: any): ISubscription {
        return {
            id: literal.id,
            presetId: literal.presetId == null ? undefined : literal.presetId,
            presetName: literal.presetName == null ? undefined : literal.presetName,
            locationId: literal.locationId,
            locationName: literal.locationName,
            canUseCovid: literal.canUseCovid,
            canVerifyCert: literal.canVerifyCert != null ? literal.canVerifyCert : undefined,
            canShowGuestNumber: literal.canShowGuestNumber != null ? literal.canShowGuestNumber : undefined,
            canContinueTracking: literal.canContinueTracking != null ? literal.canContinueTracking : undefined,
            maxDailyVisitors: literal.maxDailyVisitors != null ? parseInt(literal.maxDailyVisitors) : undefined,
            canUseOrder: literal.canUseOrder,
            canUseDocuments: literal.canUseDocuments,
            maxDocuments: literal.maxDocuments != null ? parseInt(literal.maxDocuments) : undefined,
            canUseUrl: literal.canUseUrl,
            maxLocations: literal.maxLocations != null ? parseInt(literal.maxLocations) : undefined,
            maxRooms: literal.maxRooms != null ? parseInt(literal.maxRooms) : undefined,
            maxTables: literal.maxTables != null ? parseInt(literal.maxTables) : undefined,
            created: DateTime.parseIsoDate(literal.created),
            description: literal.description != null ? literal.description : undefined,
            validUntil: literal.validUntil != null ? DateTime.parseIsoDate(literal.validUntil) : undefined,
            canUseOffer: literal.canUseOffer,
            canUsePay: literal.canUsePay,
            canUseRoomPlan: literal.canUseRoomPlan,
        };
    }

    public static parsePreset(literal: any): ISubscriptionPreset {
        return {
            id: literal.id,
            name: literal.name,
            canUseCovid: literal.canUseCovid,
            canVerifyCert: literal.canVerifyCert != null ? literal.canVerifyCert : undefined,
            canShowGuestNumber: literal.canShowGuestNumber != null ? literal.canShowGuestNumber : undefined,
            canContinueTracking: literal.canContinueTracking != null ? literal.canContinueTracking : undefined,
            maxDailyVisitors: literal.maxDailyVisitors != null ? parseInt(literal.maxDailyVisitors) : undefined,
            canUseOrder: literal.canUseOrder,
            canUseDocuments: literal.canUseDocuments,
            maxDocuments: literal.maxDocuments != null ? parseInt(literal.maxDocuments) : undefined,
            canUseUrl: literal.canUseUrl,
            maxLocations: literal.maxLocations != null ? parseInt(literal.maxLocations) : undefined,
            maxRooms: literal.maxRooms != null ? parseInt(literal.maxRooms) : undefined,
            maxTables: literal.maxTables != null ? parseInt(literal.maxTables) : undefined,
            canUseOffer: literal.canUseOffer,
            canUsePay: literal.canUsePay,
            canUseRoomPlan: literal.canUseRoomPlan,
        };
    }
}
